import { useCallback } from "react";
import { z } from "zod";
import { crmFieldValidations } from "@/shared/utils/crm-validation";
import useAddressAutocomplete from "./use-autocomplete";

/**
 * Custom hook for address validation using Google Places API
 * Validates that addresses exactly match one of the current place predictions
 *
 * This hook works with existing autocomplete functionality - it doesn't make additional API calls,
 * it simply validates that the entered address matches one of the predictions already fetched.
 *
 * @example
 * ```typescript
 * const {
 *   getPlacePredictions,
 *   placePredictions,
 *   createAddressValidationSchema,
 *   isAddressValid
 * } = useAddressValidation();
 *
 * // Create validation schema
 * const validationSchema = createAddressValidationSchema(baseSchema);
 *
 * // Use in Formik
 * const form = useFormik({
 *   validationSchema: toFormikValidationSchema(validationSchema),
 *   // ... other formik options
 * });
 *
 * // Check if current address is valid
 * const isValid = isAddressValid(form.values.address);
 * ```
 *
 * @returns {Object} Hook utilities
 * @returns {Function} validateAddress - Sync function that validates address matches current predictions
 * @returns {Function} createAddressValidationSchema - Function to create Zod schema with address validation
 * @returns {Function} getPlacePredictions - Function to get Google Places predictions
 * @returns {Array} placePredictions - Array of current place predictions
 * @returns {Function} getAddressComponents - Function to fetch a selected place's address components (street/city/state/zip)
 * @returns {Function} isAddressValid - Sync function to check if address matches current predictions
 */
export const useAddressValidation = (
  options?: Parameters<typeof useAddressAutocomplete>[0],
) => {
  const { getPlacePredictions, placePredictions, getAddressComponents } =
    useAddressAutocomplete(options);

  // Simple address validation that only checks existing placePredictions
  const validateAddress = useCallback(
    (address: string): boolean => {
      if (!address || address.trim() === "") {
        return true; // Let the required validation handle empty addresses
      }

      const trimmedAddress = address.trim();

      // Simply check if the address matches one of the current predictions
      return placePredictions.some(
        (prediction) => prediction.description === trimmedAddress,
      );
    },
    [placePredictions],
  );

  // Create a Zod schema refinement for address validation
  const createAddressValidationSchema = useCallback(
    <T extends z.ZodRawShape>(baseSchema: z.ZodObject<T>) => {
      return baseSchema.extend({
        address: z
          .string()
          .min(1, "Address is required")
          .refine(
            (address) => {
              return validateAddress(address);
            },
            {
              message: "Invalid address.",
            },
          ),
        serviceTime: z
          .number()
          .min(1, "Service duration must be at least 1 minute"),
        // Validate CRM fields (held flat in the form) when a value is present.
        ...crmFieldValidations,
      });
    },
    [validateAddress],
  );

  // Helper function to check if an address is currently valid (matches predictions)
  const isAddressValid = useCallback(
    (address: string): boolean => {
      return validateAddress(address);
    },
    [validateAddress],
  );

  return {
    validateAddress,
    createAddressValidationSchema,
    getPlacePredictions,
    placePredictions,
    getAddressComponents,
    isAddressValid,
  };
};

export default useAddressValidation;
